/**
 * AI Hub area map (V128-AI-HUB-ASSISTANT-GLOBAL).
 *
 * Pure function that maps a Next.js pathname → AreaId (the active area
 * taught by the Asistente Hub). The area ID is then passed to the backend
 * as `pageContext.section` so `ai-hub-area-knowledge.ts` can inject the
 * matching pedagogical context as a system message.
 *
 * KEEP IN SYNC with:
 *   - `packages/shared/src/types/ai-hub.ts` (AreaId union)
 *   - `backend/src/services/ai-hub-area-knowledge.ts` (HUB_AREA_KNOWLEDGE)
 *
 * Anti-P-041: no `as any`. The return type is the strict `AreaId` union —
 * the default fallback returns `'dashboard'` (a valid member of the union).
 *
 * Anti-P-383 (file size ≤500L): this file is ~150L — well under the cap.
 */

import type { AreaId } from '@profileia/shared';

/**
 * Map a Next.js pathname to the AreaId taught by the Asistente Hub.
 *
 * Strategy: simple `if/else` chain on path prefixes (no regex complexity
 * per spec §3a). Order matters — more specific routes first (e.g.
 * `/seller/dashboard` before `/seller`). The `/app/(dashboard)` route group
 * renders at `/app` (parentheses are routing groups, not URL segments).
 *
 * Unknown paths default to `'dashboard'` — the safest generic area. The
 * backend logs `ai:hub_area_context_missing` when this happens so we can
 * spot unmapped routes during QA smoke tests.
 *
 * @param pathname The Next.js router pathname (e.g. "/app/wallet").
 *                 Pass `usePathname()` directly.
 */
export function pathnameToAreaId(pathname: string): AreaId {
  // Normalize trailing slash + lowercase for robust matching.
  const p = pathname.replace(/\/+$/, '').toLowerCase();

  // ─── /app/* routes (Profileia Hub) ──────────────────────────────────
  if (p === '/app' || p === '/app/(dashboard)' || p.startsWith('/app/dashboard')) {
    return 'dashboard';
  }
  if (p.startsWith('/app/profile')) return 'profile';
  if (p.startsWith('/app/landing')) return 'landing';
  if (p.startsWith('/app/conferences')) return 'conferences';
  if (p.startsWith('/app/wallet')) return 'wallet';
  if (p.startsWith('/app/packages') || p.startsWith('/app/historial-paquetes')) {
    return 'packages';
  }
  if (p.startsWith('/app/network')) return 'network-matrix';
  if (p.startsWith('/app/analytics')) return 'analytics';
  if (p.startsWith('/app/settings')) return 'settings';
  if (p.startsWith('/app/kyc')) return 'kyc';
  if (p.startsWith('/app/academy')) return 'academy';
  if (p.startsWith('/app/almacenamiento')) return 'almacenamiento';
  if (p.startsWith('/app/conecta')) return 'conecta';
  // asistentes-ia-pro uses the same area knowledge as the free tier (both
  // teach how to use AI assistants in Profileia).
  if (p.startsWith('/app/asistentes-ia')) return 'asistentes-ia';

  // ─── /seller/* routes (vendor portal) ────────────────────────────────
  if (p.startsWith('/seller/dashboard')) return 'seller-dashboard';
  if (p.startsWith('/seller/productos')) return 'seller-productos';
  if (p.startsWith('/seller/tiendas')) return 'seller-tiendas';
  if (p.startsWith('/seller/ordenes')) return 'seller-ordenes';
  if (p.startsWith('/seller/retiros')) return 'seller-retiros';
  // /seller/onboarding + /seller/cupones + /seller/reservas + /seller/combos
  // → fall through to seller-dashboard (closest existing area).

  // ─── /market/* routes (public marketplace) ───────────────────────────
  if (p.startsWith('/market')) return 'market';

  // ─── Fallback (safe default) ─────────────────────────────────────────
  return 'dashboard';
}

/**
 * Returns the localized title + subtitle for the chat widget header based
 * on the active area. The title is the area's display name; the subtitle
 * is a generic "Te ayudo con esta área" line that adapts to the area.
 *
 * Title values mirror `HUB_AREA_KNOWLEDGE[areaId].name` on the backend —
 * duplicated here so the frontend doesn't need to fetch metadata before
 * rendering the widget (avoiding a flash of "Asistente Profileia" default
 * text on first paint).
 */
export function getAreaTitle(areaId: AreaId): { title: string; subtitle: string } {
  const areaName: Record<AreaId, string> = {
    'dashboard': 'Dashboard',
    'profile': 'Profileia Hub',
    'landing': 'Landing Pages',
    'conferences': 'Salas',
    'wallet': 'Billetera',
    'packages': 'Paquetes',
    'network-matrix': 'Matriz Viva',
    'analytics': 'Estadísticas',
    'settings': 'Configuración',
    'kyc': 'Verificación KYC',
    'academy': 'Academia',
    'almacenamiento': 'Almacenamiento',
    'conecta': 'Conecta',
    'asistentes-ia': 'Asistentes IA',
    'seller-dashboard': 'Vendedor',
    'seller-productos': 'Mis Productos',
    'seller-tiendas': 'Mis Tiendas',
    'seller-ordenes': 'Órdenes',
    'seller-retiros': 'Retiros',
    'market': 'Market',
  };

  return {
    title: `Asistente Hub · ${areaName[areaId]}`,
    subtitle: 'Pregúntame cómo usar esta área',
  };
}
